import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid, HStack, Icon, Button,
  Badge, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Stat, StatLabel, StatNumber, StatHelpText,
} from "@chakra-ui/react";
import {
  FaCheckCircle, FaArrowRight, FaClipboardList, FaUsers, FaWallet,
  FaHandshake, FaRing, FaCar, FaStethoscope, FaHome, FaPlaneDeparture,
  FaGraduationCap, FaTruck, FaTools, FaBullhorn,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const SAND = "#C2A878";
const FINANCE_PRIMARY = "#141C49";

const keyFeatures = [
  { label: "Loan Amounts", detail: "$3,000 – $80,000", icon: FaWallet },
  { label: "Flexible Repayment Terms", detail: "1 – 7 Years", icon: FaCheckCircle },
  { label: "Quick Fund Access", detail: "Within 24 Hours", icon: FaArrowRight },
  { label: "Customer-First Approach", detail: "You come first, always", icon: FaUsers },
  { label: "Personalised Interest Rates", detail: "Tailored to your profile", icon: FaHandshake },
  { label: "Ongoing Support", detail: "We're with you the whole way", icon: FaCheckCircle },
];

const processSteps = [
  {
    step: 1,
    title: "Apply Online",
    desc: "Complete our simple online application in as little as 10 minutes — no paperwork, no branch visits.",
    icon: FaClipboardList,
  },
  {
    step: 2,
    title: "Speak With Our Team",
    desc: "A dedicated finance consultant reviews your application and walks you through your best options.",
    icon: FaUsers,
  },
  {
    step: 3,
    title: "Access Your Funds",
    desc: "Once approved, funds are transferred directly to your account — fast, simple, done.",
    icon: FaWallet,
  },
];

const loanUseCases = [
  { label: "Debt Consolidation", icon: FaHandshake },
  { label: "Weddings & Events", icon: FaRing },
  { label: "Car Purchase", icon: FaCar },
  { label: "Medical Expenses", icon: FaStethoscope },
  { label: "Home Renovations", icon: FaHome },
  { label: "Travel & Holidays", icon: FaPlaneDeparture },
  { label: "Education & Training", icon: FaGraduationCap },
  { label: "Moving Costs", icon: FaTruck },
  { label: "Equipment & Tools", icon: FaTools },
  { label: "Business & Marketing", icon: FaBullhorn },
];

const partnerBenefits = [
  { label: "Earn Commission", desc: "Receive a competitive referral commission for every successful loan settled through your network." },
  { label: "No Merchant Fees", desc: "There are no fees charged to you or your customers for referrals — we handle the costs." },
  { label: "Increase Conversions", desc: "Offer your clients a fast, trusted personal finance solution and watch your conversion rates grow." },
];

const faqs = [
  {
    q: "What can I use a personal loan for?",
    a: "Personal loans can be used for almost any purpose — debt consolidation, travel, renovations, medical expenses, education, weddings, car purchases, and more. As long as the purpose is legal, we can help.",
  },
  {
    q: "How much can I borrow?",
    a: "Our personal loans range from $3,000 to $80,000, subject to your individual financial circumstances and lender assessment.",
  },
  {
    q: "Am I eligible to apply?",
    a: "To be eligible you must be at least 18 years of age, an Australian citizen or permanent resident, and have a minimum annual income of $25,000. Additional lender-specific criteria may apply.",
  },
  {
    q: "How do I apply and how long does it take?",
    a: "You can apply online in approximately 10 minutes. Once submitted, a consultant will review your application, and most customers receive a decision within 24 hours.",
  },
  {
    q: "Are there any fees involved?",
    a: "Our broking service is provided at no upfront cost to you. Lender fees (such as establishment or ongoing fees) vary by product and will be clearly disclosed before you agree to any loan.",
  },
  {
    q: "Can I repay my loan early?",
    a: "Yes. Many of our lenders allow early repayment without penalty, though some products may include early exit fees. We'll make sure you understand the terms before you commit.",
  },
  {
    q: "How is my interest rate determined?",
    a: "Your rate is based on a number of factors including your credit history, income, loan amount, and loan term. We search across our panel of 40+ lenders to find the most competitive rate available for your profile.",
  },
];

const FinanceServicesPersonalLoans: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/personal-loans"
        fallbackTitle="Personal Loans – One Finance Australia"
        fallbackDescription="Flexible personal loans from $3,000 to $80,000. Fast approvals, competitive rates, and ongoing support. Apply online in 10 minutes."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          bg: `rgba(20, 28, 73, 0.88)`,
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge bg={SAND} color={NAVY} fontSize="sm" px={4} py={2} borderRadius="full" fontWeight="700">
              Personal Finance
            </Badge>
            <Heading as="h1" size="2xl" fontWeight="bold" maxW="3xl">
              Let Us Help Bring Your Plans to Life
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Tailored personal loans from $3,000 to $80,000. Access funds fast with flexible
              repayment terms and rates personalised to you.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                as={RouterLink}
                to="/finance/apply"
                size="lg"
                bg={SAND}
                color={NAVY}
                fontWeight="700"
                rightIcon={<FaArrowRight />}
                _hover={{ opacity: 0.9 }}
              >
                Apply Now
              </Button>
              <Button
                as={RouterLink}
                to="/finance/contact"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Book a Free Consultation
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Key Features */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Text fontSize="sm" color={SAND} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
              Why Choose Us
            </Text>
            <Heading size="xl" color={NAVY}>Everything You Need in a Personal Loan</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              We've built our personal loan service around what matters most to Australian borrowers.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {keyFeatures.map((f, i) => (
              <HStack
                key={i}
                p={6}
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                spacing={4}
                _hover={{ shadow: "md", borderColor: SAND }}
                transition="all 0.2s"
              >
                <Icon as={FaCheckCircle} color={SAND} boxSize={6} flexShrink={0} />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="700" color={NAVY}>{f.label}</Text>
                  <Text fontSize="sm" color="gray.600">{f.detail}</Text>
                </VStack>
              </HStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Stats Strip */}
      <Box bg={NAVY} py={14}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            <Stat textAlign="center" color="white">
              <StatNumber fontSize="4xl" fontWeight="bold" color={SAND}>$80K</StatNumber>
              <StatLabel fontSize="sm">Maximum Loan</StatLabel>
              <StatHelpText color="gray.400">Up to $80,000</StatHelpText>
            </Stat>
            <Stat textAlign="center" color="white">
              <StatNumber fontSize="4xl" fontWeight="bold" color="white">40+</StatNumber>
              <StatLabel fontSize="sm">Lender Partners</StatLabel>
              <StatHelpText color="gray.400">From 40+ lenders</StatHelpText>
            </Stat>
            <Stat textAlign="center" color="white">
              <StatNumber fontSize="4xl" fontWeight="bold" color={SAND}>24hrs</StatNumber>
              <StatLabel fontSize="sm">Approval Time</StatLabel>
              <StatHelpText color="gray.400">Most applications</StatHelpText>
            </Stat>
            <Stat textAlign="center" color="white">
              <StatNumber fontSize="4xl" fontWeight="bold" color="white">7 Yrs</StatNumber>
              <StatLabel fontSize="sm">Max Loan Term</StatLabel>
              <StatHelpText color="gray.400">Flexible repayments</StatHelpText>
            </Stat>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Application Process */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={14}>
            <Text fontSize="sm" color={SAND} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
              Simple Process
            </Text>
            <Heading size="xl" color={NAVY}>From Application to Funded in 3 Steps</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              We've made applying for a personal loan as straightforward as possible.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {processSteps.map((s) => (
              <Box
                key={s.step}
                p={8}
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                textAlign="center"
                position="relative"
                _hover={{ shadow: "xl", transform: "translateY(-4px)", borderColor: SAND }}
                transition="all 0.3s"
              >
                <Box
                  w={14}
                  h={14}
                  borderRadius="full"
                  bg={NAVY}
                  color="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mx="auto"
                  mb={5}
                  fontSize="xl"
                  fontWeight="bold"
                >
                  {s.step}
                </Box>
                <Icon as={s.icon} boxSize={8} color={SAND} mb={4} />
                <Heading size="md" color={NAVY} mb={3}>{s.title}</Heading>
                <Text color="gray.600">{s.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Support + Use Cases */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={16} alignItems="start">
            {/* Support Beyond the Loan */}
            <VStack align="start" spacing={6}>
              <Badge bg={SAND} color={NAVY} fontSize="sm" px={3} py={1} borderRadius="full" fontWeight="700">
                Ongoing Partnership
              </Badge>
              <Heading size="xl" color={NAVY}>Support Beyond the Loan</Heading>
              <Text fontSize="lg" color="gray.600">
                Our relationship doesn't end when your funds hit your account. We monitor your loan,
                flag better deals as they emerge, and are available whenever you need advice —
                because your financial wellbeing matters long after settlement.
              </Text>
              <VStack align="start" spacing={3} w="full">
                {[
                  "Dedicated post-settlement support team",
                  "Rate review alerts when better deals appear",
                  "Guidance on early repayment and refinancing",
                  "Access to our full suite of finance tools",
                ].map((item, i) => (
                  <HStack key={i} spacing={3}>
                    <Icon as={FaCheckCircle} color={SAND} boxSize={5} flexShrink={0} />
                    <Text color="gray.700">{item}</Text>
                  </HStack>
                ))}
              </VStack>
              <Button
                as={RouterLink}
                to="/finance/contact"
                size="lg"
                bg="#C2A878"
                color="#141C49"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#b09060" }}
              >
                Talk to a Consultant
              </Button>
            </VStack>

            {/* Use Cases */}
            <VStack align="start" spacing={6}>
              <Badge bg={SAND} color={NAVY} fontSize="sm" px={3} py={1} borderRadius="full" fontWeight="700">
                Use Cases
              </Badge>
              <Heading size="xl" color={NAVY}>Personal Loans for All Your Life Goals</Heading>
              <Text color="gray.600" fontSize="lg">
                Whatever you're planning, we can help you finance it.
              </Text>
              <SimpleGrid columns={2} spacing={3} w="full">
                {loanUseCases.map((u, i) => (
                  <HStack
                    key={i}
                    p={4}
                    bg="white"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    spacing={3}
                    _hover={{ borderColor: SAND }}
                    transition="all 0.2s"
                  >
                    <Icon as={u.icon} color={SAND} boxSize={5} flexShrink={0} />
                    <Text fontSize="sm" fontWeight="600" color="gray.700">{u.label}</Text>
                  </HStack>
                ))}
              </SimpleGrid>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Partner Program */}
      <Box py={20} bg={NAVY} color="white">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Badge bg={SAND} color={NAVY} fontSize="sm" px={4} py={2} borderRadius="full" fontWeight="700">
              Partner Program
            </Badge>
            <Heading size="xl">Grow Your Business With Us</Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl">
              Refer clients to our personal loan service and benefit from our partner program —
              zero cost, real rewards.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {partnerBenefits.map((b, i) => (
              <Box
                key={i}
                p={8}
                bg="rgba(255,255,255,0.07)"
                borderRadius="xl"
                border="1px solid"
                borderColor="rgba(194,168,120,0.3)"
                _hover={{ bg: "rgba(255,255,255,0.12)" }}
                transition="all 0.2s"
              >
                <Icon as={FaCheckCircle} color={SAND} boxSize={8} mb={4} />
                <Heading size="md" mb={3} color={SAND}>{b.label}</Heading>
                <Text color="gray.300">{b.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
          <VStack mt={10}>
            <Button
              as={RouterLink}
              to="/finance/contact"
              size="lg"
              bg={SAND}
              color={NAVY}
              fontWeight="700"
              rightIcon={<FaArrowRight />}
              _hover={{ opacity: 0.9 }}
            >
              Become a Partner
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* FAQ */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Text fontSize="sm" color={SAND} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
              Common Questions
            </Text>
            <Heading size="xl" color={NAVY}>Frequently Asked Questions</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Everything you need to know about our personal loan service.
            </Text>
          </VStack>
          <Accordion allowMultiple maxW="3xl" mx="auto">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                mb={4}
                bg="white"
                shadow="sm"
                _hover={{ shadow: "md" }}
              >
                <AccordionButton py={6} px={8} _hover={{ bg: "gray.50" }} borderRadius="xl">
                  <Text flex="1" textAlign="left" fontWeight="600" color={NAVY} fontSize="md">
                    {faq.q}
                  </Text>
                  <AccordionIcon color={SAND} boxSize={6} />
                </AccordionButton>
                <AccordionPanel px={8} pb={6} pt={2} bg="gray.50" borderBottomRadius="xl">
                  <Text color="gray.700">{faq.a}</Text>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* CTA */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <Box bg={FINANCE_PRIMARY} color="white" p={14} borderRadius="2xl" textAlign="center">
            <Heading size="xl" mb={4}>Ready to Get Started?</Heading>
            <Text fontSize="lg" color="gray.300" mb={8} maxW="xl" mx="auto">
              Apply online in 10 minutes, speak with a consultant, and access your funds — all with one team by your side.
            </Text>
            <HStack spacing={4} justify="center">
              <Button
                as={RouterLink}
                to="/finance/apply"
                size="lg"
                bg={SAND}
                color={NAVY}
                fontWeight="700"
                rightIcon={<FaArrowRight />}
                _hover={{ opacity: 0.9 }}
              >
                Apply Now
              </Button>
              <Button
                as={RouterLink}
                to="/finance/contact"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "rgba(255,255,255,0.1)" }}
              >
                Book a Consultation
              </Button>
            </HStack>
          </Box>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesPersonalLoans;
